import React, { useContext, useId, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { PapiDeviation } from '@wix/da-papi-types';
import BuyCoreBrandedButton from '@wix/da-shared-react/pkg/Button/core/dsPresets/BuyCoreBrandedButton';
import { getCurrentUserHasCore } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import {
  BiData,
  SubmitAddImageClickBiEvent,
  SubmitDeleteMultiImageFileClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import FileUploadField from '@wix/da-shared-react/pkg/FileUploadField';
import { UploadType } from '@wix/da-shared-react/pkg/redux/uploads/types';
import { mimeTypeToPcpType } from '@wix/da-shared-react/pkg/utils/mime';
import type { UploadAttachmentSuccessCallback } from '@wix/da-shared-react/pkg/redux/uploads/types';
import { removeAttachment } from '@wix/da-shared-react/pkg/redux/uploads/actions';
import { putEntities } from '@wix/da-shared-react/pkg/redux/entities/actions';
import { deviationExtendedSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import { getDataEntityById } from '@wix/da-shared-react/pkg/redux/entities/selectors';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { useMaxAttachmentsAllowed } from '../../hooks/useMaxAttachmentsAllowed';
import {
  getAdditionalAttachmentAcceptedMimeTypes,
  getAdditionalAttachmentType,
} from '../../utils/additionalMediaUpload';
import { useUnlockVideoUploadsCheck } from '../UnlockWithCoreUpsellModal/useUnlockVideoUploadsCheck';

import s from './AdditionalImagesPopup.scss';

export interface Props {
  deviation: PapiDeviation;
  requestClose: () => void;
}

// TODO: Cancel uploads on modal close

const AdditionalImagesPopup: React.FC<Props> = ({ deviation, requestClose }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const fileInputId = useId();
  const hasCoreMembership = useSelector(getCurrentUserHasCore);
  const reduxStore = useStore();
  const dispatch = useDispatch();
  const isMultiVideo = useFlag('multi_video');
  const additionalAttachmentType = getAdditionalAttachmentType(isMultiVideo);
  const additionalAcceptedMimeTypes = getAdditionalAttachmentAcceptedMimeTypes(isMultiVideo);
  const logEvent = useBiLogger();
  const [items, updateItems] = useState(
    (deviation.extended?.additionalMedia || []).map(asset => ({
      name: asset.filename,
      size: asset.filesize,
      type: asset.type,
      deviationId: asset.deviationId,
      fileId: asset.fileId,
    }))
  );
  const maxAttachmentsAllowed = useMaxAttachmentsAllowed();
  const maxLimitReached = items.length >= maxAttachmentsAllowed;
  const unlockVideoAttachmentCheck = useUnlockVideoUploadsCheck();

  const handleUploadSuccess: UploadAttachmentSuccessCallback = useCallback(
    ({ deviationId, fileId, file, attachment }) => {
      const { name: fileName, size, type } = file;

      updateItems(prevItems => [
        ...prevItems,
        {
          deviationId,
          fileId,
          name: fileName,
          size,
          type: mimeTypeToPcpType(type),
        },
      ]);

      // Append attachment to extended deviation entity
      if (deviation && deviation.extended && attachment) {
        const extendedDeviationEntity = getDataEntityById(
          reduxStore.getState(),
          deviationExtendedSchema,
          deviation.deviationId
        );
        extendedDeviationEntity.additionalMedia = (
          extendedDeviationEntity.additionalMedia ?? []
        ).filter(item => item.fileId !== fileId);
        extendedDeviationEntity.additionalMedia.push(attachment);
        dispatch(
          putEntities({
            entities: {
              deviationExtended: {
                [deviation.deviationId]: extendedDeviationEntity,
              },
            },
          })
        );
      }
    },
    [reduxStore, updateItems, deviation, dispatch]
  );

  const handleRemove = useCallback(
    ({ deviationId, fileId }) => {
      if (fileId) {
        logEvent(
          BiData<SubmitDeleteMultiImageFileClickBiEvent>({
            evid: 35,
            typeid: getDeviationResourceType(deviation),
            itemid: deviation.deviationId,
            sectionname: 'attachment_popup',
            rank: items.map(item => item.fileId).indexOf(fileId),
          })
        );
        dispatch(
          removeAttachment(fileId, deviation.deviationId, additionalAttachmentType, () => {
            updateItems(prevItems => prevItems.filter(item => item.fileId !== fileId));
          })
        );
      }
    },
    [dispatch, additionalAttachmentType, updateItems, deviation, logEvent, items]
  );

  return (
    <Modal
      isOpen
      heading={t('deviationSubmit.modals.additionalImages.title')}
      headerClassName={s['modal-header']}
      contentClassName={s['modal-content']}
      hasCloseButton={!isMobile}
      showHeaderBorder
      showFooterBorder
      withScrollableContentWrapper={false}
      onRequestClose={requestClose}
      footerRight={[
        <TextButton key="cancel" variant="main" size="large" onClick={requestClose}>
          {t('common.close')}
        </TextButton>,
      ]}
    >
      <FileUploadField
        id={fileInputId}
        uploadType={UploadType.ATTACHMENT}
        uploadParams={{
          parentDeviationId: deviation.deviationId,
          attachmentType: additionalAttachmentType,
        }}
        items={items as any}
        maxItems={maxAttachmentsAllowed}
        acceptedMimeTypes={additionalAcceptedMimeTypes}
        onSuccess={handleUploadSuccess}
        onRemove={handleRemove}
        uploadsListClassName={s['uploads-list']}
        uploadButtonLabel={t('deviationSubmit.modals.additionalImages.attachBtn')}
        uploadButtonInList
        uploadButtonBiData={BiData<SubmitAddImageClickBiEvent>({
          evid: 30,
          typeid: getDeviationResourceType(deviation),
          itemid: deviation.deviationId,
          sectionname: 'attachment_popup',
          rank: items.length,
        })}
        validator={isMultiVideo ? unlockVideoAttachmentCheck.validator : undefined}
        onDropRejected={isMultiVideo ? unlockVideoAttachmentCheck.onDropRejected : undefined}
      />
      {!hasCoreMembership && maxLimitReached && (
        <BuyCoreBrandedButton size="medium" fillContainerWidth>
          {t('deviationSubmit.modals.additionalImages.attachBtn.upgrade')}
        </BuyCoreBrandedButton>
      )}
    </Modal>
  );
};
AdditionalImagesPopup.displayName = 'AdditionalImagesPopup';

export default AdditionalImagesPopup;
